import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";

interface Sitemap {
  path: string;
  lastSubmitted: string | null;
  lastDownloaded: string | null;
  isPending: boolean;
  errors: number;
  warnings: number;
  submitted: number;
  indexed: number;
}

interface GscData {
  site: string;
  totals: { submitted: number; indexed: number; errors: number; warnings: number };
  sitemaps: Sitemap[];
  fetchedAt: string;
}

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function GscPanel() {
  const [data, setData] = useState<GscData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data: res, error: err } = await supabase.functions.invoke("gsc-status");
    if (err) setError(err.message);
    else if (res?.error) setError(res.error);
    else setData(res);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Google Search Console</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Status indeksowania i map witryn dla naklejki-3d.com.pl
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Odśwież
        </Button>
      </div>

      {error && (
        <Card className="p-4 border-destructive/50 bg-destructive/5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">Błąd pobierania danych</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
          </div>
        </Card>
      )}

      {loading && !data && (
        <p className="text-sm text-muted-foreground">Ładowanie danych z Google...</p>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">URL-e zgłoszone</p>
              <p className="text-2xl font-bold text-foreground mt-1">{data.totals.submitted}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">Zindeksowane</p>
              <p className="text-2xl font-bold text-primary mt-1">{data.totals.indexed}</p>
            </Card>
            <Card className={`p-4 ${data.totals.errors > 0 ? "border-destructive/50" : ""}`}>
              <p className="text-xs uppercase text-muted-foreground">Błędy</p>
              <p className={`text-2xl font-bold mt-1 ${data.totals.errors > 0 ? "text-destructive" : "text-foreground"}`}>
                {data.totals.errors}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">Ostrzeżenia</p>
              <p className={`text-2xl font-bold mt-1 ${data.totals.warnings > 0 ? "text-amber-600" : "text-foreground"}`}>
                {data.totals.warnings}
              </p>
            </Card>
          </div>

          {/* Sitemaps list */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">
              Mapy witryn ({data.sitemaps.length})
            </h3>
            {data.sitemaps.length === 0 ? (
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Brak zgłoszonych map witryn.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {data.sitemaps.map((s) => {
                  const hasIssue = s.errors > 0 || s.warnings > 0;
                  return (
                    <Card key={s.path} className={`p-4 ${hasIssue ? "border-destructive/40" : ""}`}>
                      <div className="flex items-start gap-3 flex-wrap">
                        {hasIssue ? (
                          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <a
                            href={s.path}
                            target="_blank"
                            rel="noopener"
                            className="text-sm font-medium text-foreground hover:text-primary inline-flex items-center gap-1 break-all"
                          >
                            {s.path}
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                          <div className="flex gap-2 flex-wrap mt-2">
                            <Badge variant="secondary">Zgłoszone: {s.submitted}</Badge>
                            <Badge variant="secondary">Zindeksowane: {s.indexed}</Badge>
                            {s.errors > 0 && <Badge variant="destructive">Błędy: {s.errors}</Badge>}
                            {s.warnings > 0 && <Badge className="bg-amber-500 hover:bg-amber-500/90">Ostrzeżenia: {s.warnings}</Badge>}
                            {s.isPending && <Badge variant="outline">Oczekuje</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Ostatnio zgłoszone: {fmtDate(s.lastSubmitted)} · Pobrane przez Google: {fmtDate(s.lastDownloaded)}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Dane z {fmtDate(data.fetchedAt)}. Google aktualizuje statystyki indeksowania z opóźnieniem 24–72h.
          </p>
        </>
      )}
    </div>
  );
}
